import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getLatestRates, convertCurrency } from '../services/api'
import './ApiDemoPage.css'

const RATES_QUERY_KEY = ['frankfurter', 'rates'] as const
const CONVERT_QUERY_KEY = ['frankfurter', 'convert'] as const

const DEFAULT_SYMBOLS = ['USD', 'GBP', 'CHF', 'JPY']

const ApiDemoPage: React.FC = () => {
  const [ratesBase, setRatesBase] = useState<string | null>(null)
  const [convertFrom] = useState('USD')
  const [convertTo] = useState('EUR')
  const [amount, setAmount] = useState('100')
  const [convertTrigger, setConvertTrigger] = useState(0)

  const {
    data: latestRates,
    isLoading: loadingRates,
    isError: errorRates,
    error: ratesError,
  } = useQuery({
    queryKey: [...RATES_QUERY_KEY, ratesBase],
    queryFn: () => getLatestRates(ratesBase!, DEFAULT_SYMBOLS),
    enabled: ratesBase !== null,
  })

  const {
    data: conversion,
    isLoading: loadingConvert,
    isError: errorConvert,
    error: convertError,
  } = useQuery({
    queryKey: [...CONVERT_QUERY_KEY, convertFrom, convertTo, amount, convertTrigger],
    queryFn: () => convertCurrency(convertFrom, convertTo, parseFloat(amount)),
    enabled: convertTrigger > 0 && !Number.isNaN(parseFloat(amount)) && parseFloat(amount) > 0,
  })

  const ratesErrorMessage = ratesError && typeof ratesError === 'object' && 'message' in ratesError
    ? (ratesError as { message: string }).message
    : errorRates
      ? 'Request failed'
      : null
  const convertErrorMessage = convertError && typeof convertError === 'object' && 'message' in convertError
    ? (convertError as { message: string }).message
    : errorConvert
      ? 'Request failed'
      : null

  return (
    <div className="api-demo-page">
      <div className="api-demo-hero">
        <h1>Currency API Demo</h1>
        <p>
          Built with <strong>TanStack Query</strong>. Data from{' '}
          <strong>Frankfurter</strong> — European Central Bank reference rates.
          No API key, no tracking, no usage caps.
        </p>
      </div>

      {(ratesErrorMessage || convertErrorMessage) && (
        <div className="api-demo-error" role="alert">
          {ratesErrorMessage || convertErrorMessage}
        </div>
      )}

      <div className="api-demo-actions">
        <section className="api-demo-card">
          <h2>Latest rates</h2>
          <p>Fetch latest exchange rates (base: USD). ECB data via Frankfurter.</p>
          <button
            type="button"
            onClick={() => setRatesBase('USD')}
            disabled={loadingRates}
            className="api-demo-button"
          >
            {loadingRates ? 'Loading…' : 'Fetch latest USD rates'}
          </button>
          {latestRates && (
            <div className="api-demo-result">
              <h3>1 {latestRates.base} = (as of {latestRates.date})</h3>
              <ul className="api-demo-rates-list">
                {Object.entries(latestRates.rates).map(([code, rate]) => (
                  <li key={code}>
                    <strong>{code}</strong>: {rate.toFixed(4)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <section className="api-demo-card">
          <h2>Convert amount</h2>
          <p>Convert from USD to EUR using the latest rate.</p>
          <div className="api-demo-convert-form">
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Amount"
              className="api-demo-input"
              aria-label="Amount to convert"
            />
            <span className="api-demo-convert-label">USD → EUR</span>
            <button
              type="button"
              onClick={() => setConvertTrigger((c) => c + 1)}
              disabled={loadingConvert || !amount || Number.isNaN(parseFloat(amount))}
              className="api-demo-button"
            >
              {loadingConvert ? 'Converting…' : 'Convert'}
            </button>
          </div>
          {conversion && (
            <div className="api-demo-result">
              <p className="api-demo-convert-result">
                {conversion.amount} {conversion.from} = <strong>{conversion.result} {conversion.to}</strong>
              </p>
              <span className="api-demo-meta">Rate: 1 {conversion.from} = {conversion.rate} {conversion.to} (date: {conversion.date})</span>
            </div>
          )}
        </section>
      </div>

      <div className="api-demo-footer">
        <p>
          Data from{' '}
          <a href="https://www.frankfurter.app/docs/" target="_blank" rel="noreferrer">
            Frankfurter
          </a>
          {' '}(European Central Bank reference rates). No API key, no tracking. API in <code>src/services/api.ts</code>; state with <code>@tanstack/react-query</code>.
        </p>
      </div>
    </div>
  )
}

export default ApiDemoPage